/**
 * EinladungPage — the broker-employee invitation activation page /einladung
 * (Story ADMIN-W4 AC 3; the W2 mail links here with ?token=…).
 *
 * Shell-less branded page: loads the invitation preview (firm + invited
 * identity), collects password ×2 (client-side mirror checks ONLY — the server
 * re-validates at the DTO edge), posts the activation, and on success offers
 * "Zur Anmeldung" (starts the OIDC flow; first login then walks through the
 * Keycloak TOTP setup). A 401 shows the uniform error — wrong/expired/consumed
 * links are indistinguishable. The token appears ONLY in the request bodies —
 * never in logs or error copy.
 *
 * The preview/activate calls go through apiFetch with `auth: false`: the routes
 * are @Public(), no bearer is ever attached, and their uniform 401 (invalid
 * token) NEVER triggers the session-refresh/redirect path — a logged-in user
 * opening a stale link keeps their session and sees the uniform error here.
 *
 * [Source: apps/web/src/portal/features/auth/PortalActivate.tsx] (structure precedent)
 */

import { useEffect, useState } from 'react';
import type { BrokerActivationPreviewResponse } from '@brokly/shared';
import { BROKER_PASSWORD_MIN_LENGTH } from '@brokly/shared';
import { apiFetch } from '../../lib/api-client';
import { OIDC_IDP_MICROSOFT } from '../../lib/auth/oidc';
import { startLogin } from './useBrokerAuth';

const TOKEN_ERROR =
  'Dieser Einladungslink ist ungültig oder abgelaufen. Bitte wenden Sie sich an Ihre Administrator:in für eine neue Einladung.';
const MISMATCH_ERROR = 'Die Passwörter stimmen nicht überein.';
const TOO_SHORT_ERROR = `Das Passwort muss mindestens ${BROKER_PASSWORD_MIN_LENGTH} Zeichen lang sein.`;

type Phase = 'loading' | 'invalid' | 'form' | 'activated';

export function EinladungPage({ token }: { token?: string }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [preview, setPreview] = useState<BrokerActivationPreviewResponse | null>(null);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      return;
    }
    apiFetch<BrokerActivationPreviewResponse>('/auth/broker-activation/preview', {
      method: 'POST',
      body: JSON.stringify({ token }),
      auth: false, // public route — never the session/refresh/redirect path
    })
      .then((res) => {
        setPreview(res);
        setPhase('form');
      })
      .catch(() => setPhase('invalid'));
  }, [token]);

  /**
   * Passwordless SSO activation (WAVE2-SSO): provision the account with no
   * password, then jump STRAIGHT to Microsoft. Keycloak links the Entra identity
   * to the just-provisioned account by email (the realm auto-link flow skips the
   * "Account already exists" page), and the employee never invents a password.
   */
  const submitSso = async () => {
    if (!consent || !token) {
      return;
    }
    setSsoBusy(true);
    setError(null);
    try {
      await apiFetch('/auth/broker-activation/sso', {
        method: 'POST',
        body: JSON.stringify({ token }),
        auth: false, // public route — never the session/refresh/redirect path
      });
      // Success → redirect to Microsoft (navigates away; no state reset needed).
      await startLogin('/', OIDC_IDP_MICROSOFT);
    } catch {
      // Uniform copy — wrong/expired/consumed are indistinguishable by design.
      setError(TOKEN_ERROR);
      setSsoBusy(false);
    }
  };

  const submit = async () => {
    // Required consent gate (defends against Enter-submit bypassing the disabled button).
    if (!consent) {
      return;
    }
    // Client-side UX mirrors only — the server re-validates (rule #2).
    if (password.length < BROKER_PASSWORD_MIN_LENGTH) {
      setError(TOO_SHORT_ERROR);
      return;
    }
    if (password !== repeat) {
      setError(MISMATCH_ERROR);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/auth/broker-activation', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
        auth: false, // public route — never the session/refresh/redirect path
      });
      setPhase('activated');
    } catch {
      // Uniform copy — wrong/expired/consumed are indistinguishable by design.
      setError(TOKEN_ERROR);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-sm">
        {/* Brand (mirrors the AppShell brand row) */}
        <div className="mb-6 flex items-center gap-2">
          <span className="grid size-8 place-items-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
            B
          </span>
          <span className="text-base font-semibold">Brokly</span>
        </div>

        {phase === 'loading' && (
          <p className="text-sm text-muted-foreground">Einladung wird geprüft…</p>
        )}

        {phase === 'invalid' && (
          <div role="alert">
            <h1 className="text-base font-semibold">Einladung ungültig</h1>
            <p className="mt-2 text-sm text-muted-foreground">{TOKEN_ERROR}</p>
          </div>
        )}

        {phase === 'activated' && (
          <div>
            <h1 className="text-base font-semibold">Zugang aktiviert</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Ihr Zugang ist eingerichtet. Melden Sie sich jetzt an. Beim ersten Login richten Sie
              die Zwei-Faktor-Authentifizierung ein.
            </p>
            <button
              type="button"
              onClick={() => void startLogin('/')}
              className="mt-4 w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Zur Anmeldung
            </button>
          </div>
        )}

        {phase === 'form' && preview && (
          <div>
            <h1 className="text-base font-semibold">Willkommen bei {preview.firmName}</h1>
            <p className="mt-1 mb-4 text-sm text-muted-foreground">
              Sie wurden als <span className="font-medium">{preview.displayName}</span> (
              {preview.email}) eingeladen.
              {preview.invitedByName ? ` Eingeladen von ${preview.invitedByName}.` : ''} Aktivieren
              Sie Ihren Zugang, am einfachsten mit Ihrem Microsoft-Konto oder mit einem Passwort.
            </p>

            {error && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            {/* Required consent — shared gate for BOTH activation paths (SSO + password). */}
            <label
              htmlFor="einladung-consent"
              className="mb-4 flex items-start gap-2 text-xs text-muted-foreground"
            >
              <input
                id="einladung-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 rounded border-input accent-primary"
              />
              <span>
                Ich akzeptiere die{' '}
                <a
                  href="https://brokly.ch/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Datenschutzerklärung
                </a>{' '}
                und die{' '}
                <a
                  href="https://brokly.ch/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Nutzungsbedingungen
                </a>
                .
              </span>
            </label>

            {/* Passwordless SSO — the recommended path (no password to invent). */}
            <button
              type="button"
              onClick={() => void submitSso()}
              disabled={!consent || ssoBusy || busy}
              className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {ssoBusy ? 'Weiterleitung …' : 'Mit Microsoft anmelden'}
            </button>

            {/* Divider */}
            <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              oder mit Passwort
              <span className="h-px flex-1 bg-border" />
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <label htmlFor="einladung-password" className="block text-sm font-medium">
                Passwort
              </label>
              <input
                id="einladung-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={BROKER_PASSWORD_MIN_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring"
                placeholder={`Mindestens ${BROKER_PASSWORD_MIN_LENGTH} Zeichen`}
              />

              <label htmlFor="einladung-repeat" className="mt-4 block text-sm font-medium">
                Passwort wiederholen
              </label>
              <input
                id="einladung-repeat"
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring"
                placeholder="Passwort erneut eingeben"
              />

              <button
                type="submit"
                disabled={busy || ssoBusy || !password || !repeat || !consent}
                className="mt-4 w-full rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary/50 disabled:opacity-50"
              >
                {busy ? 'Aktivieren…' : 'Mit Passwort aktivieren'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
